import {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
  WithFieldValue,
} from 'firebase/firestore'
import { ShipmentPlan, ShipmentPlanKind, ShipmentPlanLocation, ShipmentLeg, ShipmentLegStatus, ShipmentPlanVehicleDamage, LegFile, getShipmentPlanKind } from '../logistics/logistics'

function locationsToFirestore(locations: ShipmentPlanLocation[]): Record<string, unknown>[] {
  return locations.map((loc) => ({
    location_id: loc.location_id ?? '',
    location_type: loc.location_type ?? 'pickup',
    notes: loc.notes ?? null,
  }))
}

function gatePassesToFirestore(gatePasses: LegFile[]): Record<string, unknown>[] {
  return gatePasses.map((f) => ({
    fileName: f.fileName ?? '',
    storagePath: f.storagePath ?? '',
    url: f.url ?? '',
    fileSize: f.fileSize ?? 0,
    fileType: f.fileType ?? '',
    uploadedAt: f.uploadedAt ?? Timestamp.now(),
    uploadedBy: f.uploadedBy ?? '',
  }))
}

function legsToFirestore(legs: ShipmentLeg[]): Record<string, unknown>[] {
  return legs.map((leg) => ({
    from_id: leg.from_id ?? '',
    from_type: leg.from_type ?? 'pickup',
    to_id: leg.to_id ?? '',
    to_type: leg.to_type ?? 'pickup',
    company_id: leg.company_id ?? null,
    company_history: (leg.company_history ?? []).map((h) => ({
      company_id: h.company_id ?? '',
      timestamp: h.timestamp ?? Timestamp.now(),
    })),
    driver_id: leg.driver_id ?? null,
    assignment_notes: leg.assignment_notes ?? null,
    assignment_notes_photo: leg.assignment_notes_photo
      ? {
          fileName: leg.assignment_notes_photo.fileName ?? '',
          storagePath: leg.assignment_notes_photo.storagePath ?? '',
          url: leg.assignment_notes_photo.url ?? '',
          fileSize: leg.assignment_notes_photo.fileSize ?? 0,
          fileType: leg.assignment_notes_photo.fileType ?? '',
          uploadedAt: leg.assignment_notes_photo.uploadedAt ?? Timestamp.now(),
          uploadedBy: leg.assignment_notes_photo.uploadedBy ?? '',
        }
      : null,
    dispatch_info: leg.dispatch_info ?? null,
    estimated_shipping_price: leg.estimated_shipping_price ?? null,
    scheduled_pickup_date: leg.scheduled_pickup_date ?? null,
    ready_date: leg.ready_date ?? null,
    ready: leg.ready ?? null,
    rejected: leg.rejected ?? false,
    admin_completion_requested: leg.admin_completion_requested ?? false,
    charge_cancelled: leg.charge_cancelled ?? false,
    charge_cancelled_note: leg.charge_cancelled_note ?? null,
    charge_cancelled_at: leg.charge_cancelled_at ?? null,
    charge_cancelled_by: leg.charge_cancelled_by ?? null,
    status_history: (leg.status_history ?? []).map((h) => ({
      status: h.status ?? 'Draft',
      updated_at: h.updated_at ?? Timestamp.now(),
      notes: h.notes ?? null,
    })),
    gate_passes: gatePassesToFirestore(leg.gate_passes ?? []),
  }))
}

export const shipmentPlanConverter: FirestoreDataConverter<ShipmentPlan> = {
  toFirestore(plan: WithFieldValue<ShipmentPlan>) {
    const rawLocations = plan.locations ?? []
    const locations = Array.isArray(rawLocations)
      ? locationsToFirestore(rawLocations as ShipmentPlanLocation[])
      : []
    const rawLegs = plan.shipment_legs ?? []
    const legs = Array.isArray(rawLegs)
      ? legsToFirestore(rawLegs as ShipmentLeg[])
      : []
    return {
      transport_truck_id: plan.transport_truck_id ?? '',
      client_user_id: plan.client_user_id ?? null,
      description: plan.description ?? null,
      plan_kind: getShipmentPlanKind(plan as ShipmentPlan),
      locations,
      shipment_legs: legs,
      created_at: plan.created_at ?? Timestamp.now(),
      created_by: plan.created_by ?? '',
      updated_at: plan.updated_at ?? Timestamp.now(),
    }
  },

  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions
  ): ShipmentPlan {
    const data = snapshot.data(options)
    const rawLocations = data.locations ?? []
    const locations: ShipmentPlanLocation[] = Array.isArray(rawLocations)
      ? rawLocations.map((loc: any) => {
          const rawType = loc.location_type ?? 'pickup'
          const locationType = rawType === 'repair_shop' ? 'repair' : rawType
          return {
            location_id: loc.location_id ?? '',
            location_type: locationType as ShipmentPlanLocation['location_type'],
            notes: loc.notes ?? undefined,
          }
        })
      : []
    const rawLegs = data.shipment_legs ?? []
    const shipment_legs: ShipmentLeg[] = Array.isArray(rawLegs)
      ? rawLegs.map((leg: any) => {
          const rawFrom = leg.from_type ?? 'pickup'
          const rawTo = leg.to_type ?? 'pickup'
          const rawHistory = leg.status_history ?? []
          const rawStatus = leg.status ?? 'Draft'
          const allowedStatuses = ['Draft', 'Pending driver', 'Pending completion', 'Assigned', 'In progress', 'Completed']
          const statusHistory = Array.isArray(rawHistory) && rawHistory.length > 0
            ? rawHistory
                .filter((h: any) => allowedStatuses.includes(h.status))
                .map((h: any) => ({
                  status: (allowedStatuses.includes(h.status) ? h.status : 'Draft') as ShipmentLegStatus,
                  updated_at: h.updated_at ?? Timestamp.now(),
                  notes: h.notes ?? undefined,
                }))
            : [{ status: (allowedStatuses.includes(rawStatus) ? rawStatus : 'Draft') as ShipmentLegStatus, updated_at: Timestamp.now(), notes: 'Shipment leg created' }]
          const rawGatePasses = leg.gate_passes ?? []
          const gate_passes: LegFile[] = Array.isArray(rawGatePasses)
            ? rawGatePasses.map((f: any) => {
                const rawAt = f.uploadedAt
                const uploadedAt =
                  rawAt && typeof rawAt.toDate === 'function'
                    ? (rawAt as Timestamp)
                    : Timestamp.now()
                return {
                  fileName: String(f.fileName ?? ''),
                  storagePath: String(f.storagePath ?? ''),
                  url: String(f.url ?? ''),
                  fileSize: Number(f.fileSize ?? 0),
                  fileType: String(f.fileType ?? ''),
                  uploadedAt,
                  uploadedBy: String(f.uploadedBy ?? ''),
                }
              })
            : []
          return {
            from_id: leg.from_id ?? '',
            from_type: (rawFrom === 'repair_shop' ? 'repair' : rawFrom) as ShipmentPlanLocation['location_type'],
            to_id: leg.to_id ?? '',
            to_type: (rawTo === 'repair_shop' ? 'repair' : rawTo) as ShipmentPlanLocation['location_type'],
            company_id: leg.company_id ?? undefined,
            company_history: Array.isArray(leg.company_history)
              ? leg.company_history
                  .filter((h: any) => h && typeof h.company_id === 'string')
                  .map((h: any) => ({
                    company_id: h.company_id,
                    timestamp: h.timestamp ?? Timestamp.now(),
                  }))
              : undefined,
            driver_id: leg.driver_id ?? undefined,
            assignment_notes: leg.assignment_notes ?? undefined,
            assignment_notes_photo: (() => {
              const raw = leg.assignment_notes_photo
              if (!raw || typeof raw !== 'object') return undefined
              const rawAt = (raw as any).uploadedAt
              const uploadedAt =
                rawAt && typeof rawAt.toDate === 'function'
                  ? (rawAt as Timestamp)
                  : Timestamp.now()
              return {
                fileName: String((raw as any).fileName ?? ''),
                storagePath: String((raw as any).storagePath ?? ''),
                url: String((raw as any).url ?? ''),
                fileSize: Number((raw as any).fileSize ?? 0),
                fileType: String((raw as any).fileType ?? ''),
                uploadedAt,
                uploadedBy: String((raw as any).uploadedBy ?? ''),
              }
            })(),
            dispatch_info: leg.dispatch_info ?? undefined,
            estimated_shipping_price: leg.estimated_shipping_price ?? undefined,
            scheduled_pickup_date: leg.scheduled_pickup_date ?? undefined,
            ready_date: leg.ready_date ?? undefined,
            ready: leg.ready ?? undefined,
            rejected: leg.rejected === true,
            admin_completion_requested: leg.admin_completion_requested === true,
            charge_cancelled: leg.charge_cancelled === true,
            charge_cancelled_note:
              typeof leg.charge_cancelled_note === 'string' ? leg.charge_cancelled_note : undefined,
            charge_cancelled_at: leg.charge_cancelled_at ?? undefined,
            charge_cancelled_by:
              typeof leg.charge_cancelled_by === 'string' ? leg.charge_cancelled_by : undefined,
            status_history: statusHistory,
            gate_passes,
          }
        })
      : []
    const rawDamage = data.vehicle_damage
    const vehicle_damage: ShipmentPlanVehicleDamage | undefined =
      rawDamage && typeof rawDamage === 'object' && typeof rawDamage.has_damage === 'boolean'
        ? {
            description: String(rawDamage.description ?? ''),
            has_damage: Boolean(rawDamage.has_damage),
            has_working_cameras: Boolean(rawDamage.has_working_cameras),
            has_shelves: Boolean(rawDamage.has_shelves),
            repair_shop_notes:
              typeof rawDamage.repair_shop_notes === 'string'
                ? rawDamage.repair_shop_notes
                : undefined,
            reported_at: rawDamage.reported_at ?? Timestamp.now(),
            reported_by: String(rawDamage.reported_by ?? ''),
          }
        : undefined

    const rawKind = data.plan_kind
    const plan_kind: ShipmentPlanKind =
      rawKind === 'post_delivery_repair' ? 'post_delivery_repair' : 'standard'

    return {
      id: snapshot.id,
      transport_truck_id: data.transport_truck_id ?? '',
      client_user_id: data.client_user_id ?? undefined,
      description: data.description ?? undefined,
      plan_kind,
      locations,
      shipment_legs,
      vehicle_damage,
      created_at: data.created_at ?? Timestamp.now(),
      created_by: data.created_by ?? '',
      updated_at: data.updated_at ?? Timestamp.now(),
    }
  },
}
